import React, { useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { size } from "lodash";
import moment from "moment";

import { getPayPeriodExamples } from "services/employerService";
import type {
  GetPayPeriodExamplesQuery,
  GetPayPeriodExamplesQueryVariables,
} from "types/graphql";
import IconTable from "./IconTable";
import { formatDate } from "../utils/timeHelper";

// Element type of the service's getPayPeriodExamples result.
type PayPeriodExample = NonNullable<
  NonNullable<GetPayPeriodExamplesQuery["getPayPeriodExamples"]>[number]
>;

interface PayPeriodExamplesOwnProps {
  // Component — props a parent passes (propTypes minus `client`, which withApollo injects)
  payrollFrequency: string;
  dayOne?: number;
  dayTwo?: number;
  anchorPayDate?: string;
  displayDayOne?: number;
  displayDayTwo?: number;
  displayAnchorPayDate?: string;
  payrollProcessingOffset?: number;

  // Lifecycle methods
  onComplete?: () => void;
}

const PayPeriodExamples = ({
  client,
  payrollFrequency,
  dayOne,
  dayTwo,
  anchorPayDate,
  displayDayOne,
  displayDayTwo,
  displayAnchorPayDate,
  payrollProcessingOffset,
  onComplete,
}: WithApolloClient<PayPeriodExamplesOwnProps>) => {
  const [payPeriods, setPayPeriods] = React.useState<PayPeriodExample[]>([]);

  const formattedPayPeriods = React.useMemo(() => {
    const format = "MM/DD/YYYY";
    return payPeriods.map((example) => ({
      ...example,
      payPeriodStart: formatDate(example.payPeriodStart, format),
      payPeriodEnd: formatDate(example.payPeriodEnd, format),
      payrollProcessingDate: formatDate(example.payrollProcessingDate, format),
    }));
  }, [payPeriods]);

  useEffect(() => {
    const fetchExamples = async () => {
      try {
        const periods = await getPayPeriodExamples(
          client as ApolloClient<object>,
          {
            // payrollFrequency arrives as a plain string from the parent but the
            // service's ScheduleRequestInput expects a Frequency enum, and
            // anchorPayDate may be null though the input types it as required —
            // cast the request object once at this typed-service boundary.
            payrollFrequency,
            dayOne,
            dayTwo,
            anchorPayDate: anchorPayDate
              ? moment(anchorPayDate).format("YYYY-MM-DD")
              : null,
            displayDayOne,
            displayDayTwo,
            displayAnchorPayDate: displayAnchorPayDate
              ? moment(displayAnchorPayDate).format("YYYY-MM-DD")
              : null,
            payrollProcessingOffset,
          } as GetPayPeriodExamplesQueryVariables["input"]
        );
        // periods is `Array<...> | null` from the service; the original code
        // calls .slice on it directly (non-null assertion preserves that), and
        // the element type may contain nulls which the original mapping ignored.
        setPayPeriods(periods!.slice(0, 5) as PayPeriodExample[]); // supports up to 10 examples
      } catch (error) {
        console.error({ message: "Error fetching pay period examples", error });
      } finally {
        if (onComplete) {
          onComplete();
        }
      }
    };
    void fetchExamples();
  }, [
    payrollFrequency,
    dayOne,
    dayTwo,
    anchorPayDate,
    displayDayOne,
    displayDayTwo,
    displayAnchorPayDate,
    payrollProcessingOffset,
  ]);

  if (size(payPeriods) === 0) {
    // Swallow any and all errors
    return null;
  }

  return (
    <div>
      <h6>Example Pay Periods</h6>
      <IconTable
        columns={[
          { label: "Start", key: "payPeriodStart" },
          { label: "End", key: "payPeriodEnd" },
          { label: "Processing Date", key: "payrollProcessingDate" },
        ]}
        data={formattedPayPeriods}
      />
    </div>
  );
};

export default withApollo<PayPeriodExamplesOwnProps>(PayPeriodExamples);
